import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, MapPin, GraduationCap, BadgePercent } from "lucide-react";
import { waLink } from "@/lib/whatsapp";
import { CodeWindow } from "./CodeWindow";

const LINE_EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: LINE_EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const PILLS = [
    { icon: MapPin, text: "Atendimento presencial no Campo Limpo" },
    { icon: GraduationCap, text: "Formação superior em TADS" },
    { icon: BadgePercent, text: "Zero taxas abusivas de delivery" },
];

export const Hero = () => {
    const { scrollY } = useScroll();
    const yGlow = useTransform(scrollY, [0, 700], [0, 140]);
    const yCard = useTransform(scrollY, [0, 700], [0, -60]);

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative overflow-hidden pb-20 pt-36 sm:pt-44 lg:pb-28"
        >
            <div className="bg-grid grid-fade absolute inset-0" aria-hidden="true" />
            <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -top-32 left-1/2 h-[480px] w-[720px] -translate-x-1/2 rounded-full bg-[#0088FF]/[0.13] blur-[130px]" />
                <div className="absolute right-[-160px] top-40 h-[380px] w-[380px] rounded-full bg-[#00D2FF]/[0.08] blur-[110px]" />
                <div className="absolute bottom-0 left-[-140px] h-[320px] w-[320px] rounded-full bg-[#25D366]/[0.06] blur-[110px]" />
            </motion.div>

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: LINE_EASE }}
                        data-testid="hero-eyebrow"
                        className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-4 py-2"
                    >
                        <span className="beacon-dot h-2 w-2 rounded-full bg-[#25D366]" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">
                            Campo Limpo · Zona Sul de SP
                        </span>
                    </motion.div>

                    <h1 className="text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-[4.15rem]">
                        <MaskedLine delay={0.1}>Tecnologia, Suporte</MaskedLine>
                        <MaskedLine delay={0.22}>de TI e Criação de Sites</MaskedLine>
                        <MaskedLine delay={0.34}>
                            <span className="text-glow-cyan bg-gradient-to-r from-cyan-300 via-[#00D2FF] to-[#0088FF] bg-clip-text text-transparent">
                                no Campo Limpo.
                            </span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55, ease: LINE_EASE }}
                        data-testid="hero-description"
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                    >
                        Criamos o cardápio digital e o site do seu negócio e cuidamos
                        dos seus computadores — tudo com atendimento direto, rápido e
                        sem burocracia para o comércio do Campo Limpo e da Zona Sul de SP.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.7, ease: LINE_EASE }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
                    >
                        <a
                            href={waLink("general")}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-base font-bold text-[#052e12] transition-all duration-300 hover:-translate-y-1 hover:bg-[#2ce06f] hover:shadow-[0_14px_44px_rgba(37,211,102,0.4)]"
                        >
                            <MessageCircle size={20} strokeWidth={2.5} className="transition-transform duration-300 group-hover:rotate-6" />
                            Falar no WhatsApp Agora
                        </a>
                        <a
                            href="#servicos"
                            data-testid="hero-services-anchor"
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 px-8 py-4 text-base font-semibold text-slate-200 transition-all duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
                        >
                            Ver Nossos Serviços
                            <ArrowDown size={17} />
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
                        data-testid="hero-trust-pills"
                    >
                        {PILLS.map((p) => (
                            <li key={p.text} className="flex items-center gap-2 text-sm text-slate-500">
                                <p.icon size={15} className="text-cyan-400" />
                                {p.text}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <motion.div
                    style={{ y: yCard }}
                    initial={{ opacity: 0, y: 48, rotate: 2 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: LINE_EASE }}
                    className="relative hidden lg:block"
                    data-testid="hero-visual"
                >
                    <CodeWindow />
                    <div className="absolute -right-4 -top-4 -z-10 h-full w-full rounded-3xl border border-cyan-400/10" aria-hidden="true" />
                </motion.div>
            </div>
        </section>
    );
};
